// © Microsoft Corporation. All rights reserved.

/** Flag to keep track of service worker "enabled" status */
let serviceWorkerEnabled = false;

/**
 * Purge caches with the given cache name.
 * @param cacheNameToPurge The name of the cache to purge.
 */
async function purgeCacheAsync(cacheNameToPurge: ServiceWorkerCacheName): Promise<void> {
    if (this.isServiceWorkerActive()) {
        const cacheNames = await caches.keys();
        const cacheNamesToDelete = cacheNames.filter(cacheName => cacheName.includes(cacheNameToPurge));
        await Promise.all(cacheNamesToDelete.map((cacheName) => caches.delete(cacheName)));
    }
}

/**
 * Purge cache entry for given storage and request
 * @param store Cache storage that entry is being purged from
 * @param url Url of request to delete from cache storage
 * @param method Method of request to delete from cache storage
 */
async function purgeEntryInCache(store: string, url: string, method: string): Promise<void> {
    if (this.isServiceWorkerActive()) {
        const cacheStorage = await caches.open(store);
        const cacheEntries = await cacheStorage.keys();
        const cacheEntriesToDelete = cacheEntries.filter((cacheEntry) => {
            return cacheEntry.url.startsWith(url) && cacheEntry.method === method;
        });
        await Promise.all(cacheEntriesToDelete.map((cacheEntry) => cacheStorage.delete(cacheEntry)));
    }
}

/**
 * Updates service worker enabled status
 */
function setIsServiceWorkerEnabled(enabled: boolean) {
    serviceWorkerEnabled = enabled;
}

/**
 * Checks whether service worker is "enabled"
 */
function getIsServiceWorkerEnabled(): boolean {
    return serviceWorkerEnabled;
}

/**
 * Check if serviceWorker is in "activated" state
 */
function isServiceWorkerActive(): boolean {
    const { navigator } = window;

    // Get the latest instance of controller
    const serviceWorkerController = navigator
        && navigator.serviceWorker
        && navigator.serviceWorker.controller;

    return serviceWorkerController && serviceWorkerController.state === "activated";
}

/** Helper methods for service worker cache. */
export const serviceWorkerCacheHelper = {
    purgeCacheAsync: purgeCacheAsync,
    purgeEntryInCache: purgeEntryInCache,
    isServiceWorkerActive: isServiceWorkerActive,
    getIsServiceWorkerEnabled: getIsServiceWorkerEnabled,
    setIsServiceWorkerEnabled: setIsServiceWorkerEnabled
};

export enum ServiceWorkerCacheName {
    base = "base",
    followedTopics = "followedTopics",
    river = "river"
}